import { getCertaintyCoeff } from '../config/scoringConfig';

/**
 * Calcul du score pondéré pour les QCM en mode exam style
 * 
 * @param {Set} selectedAnswers - Les réponses sélectionnées par l'utilisateur
 * @param {Array} correctAnswers - Les réponses correctes (peut être un array de strings ou d'objets avec propriété 'answer')
 * @param {number} attemptCount - Nombre de tentatives
 * @param {number} elapsedTime - Temps écoulé en secondes
 * @param {number} maxTime - Temps maximum attendu en secondes
 * @param {Array} certainties - Array de niveaux de certitude ('high', 'medium', 'low') pour chaque réponse sélectionnée
 * @returns {Object|null} - { score: number, maxScore: number } ou null si pas de données
 */
export const calculateWeightedScore = (
    selectedAnswers,
    correctAnswers,
    attemptCount = 1,
    elapsedTime = 0,
    maxTime = 0,
    certainties = []
) => {
    // Vérifier que nous avons des données valides
    if (!selectedAnswers || selectedAnswers.size === 0 || !correctAnswers || correctAnswers.length === 0) {
        return null;
    }

    // Normaliser correctAnswers : peut être un array de strings ou d'objets avec propriété 'answer'
    const correctAnswersArray = correctAnswers.map(ca => 
        typeof ca === 'string' ? ca : (ca?.answer || ca)
    );
    const correctAnswersSet = new Set(correctAnswersArray);
    
    // Trouver les réponses correctes sélectionnées
    const correctSelected = [...selectedAnswers].filter(answer => correctAnswersSet.has(answer));
    
    // Base score: nombre de bonnes réponses / nombre de bonnes réponses attendues
    const baseScore = correctSelected.length / correctAnswers.length;
    
    // Coefficients selon la spécification
    // attempt_coeff = min(1/(nb_attempt-0.5), 1)
    const safeAttemptCount = Math.max(attemptCount || 1, 1);
    const attemptCoeff = Math.min(1 / (safeAttemptCount - 0.5), 1);
    
    // duration_coeff = min(expected_time/(elapsed_time+expected_time/10), 1)
    const durationCoeff = maxTime > 0 
        ? Math.min(maxTime / (elapsedTime + maxTime / 10), 1)
        : 1;
    
    let certaintyCoeff = 1;
    if (Array.isArray(certainties) && certainties.length > 0) {
        const certaintyValues = certainties.map((c) => getCertaintyCoeff(c));
        certaintyCoeff = certaintyValues.reduce((sum, val) => sum + val, 0) / certaintyValues.length;
    } else if (typeof certainties === 'string') {
        certaintyCoeff = getCertaintyCoeff(certainties);
    }
    
    const rawScore = Math.floor(baseScore * attemptCoeff * durationCoeff * 10);
    
    // Score pondéré (pour l'instant identique au raw score)
    const weightedScore = rawScore;
    
    // Retourner au format attendu par InfoPanel: { score, maxScore }
    return {
        score: weightedScore,
        maxScore: 10, // Score maximum sur 10
        raw: rawScore,
        weighted: weightedScore,
        correctSelected: correctSelected.length,
        totalCorrect: correctAnswers.length,
        incorrectSelected: selectedAnswers.size - correctSelected.length,
        // Détails des coefficients pour debug
        coefficients: {
            baseScore,
            attemptCoeff,
            durationCoeff,
            certaintyCoeff,
            final: baseScore * attemptCoeff * durationCoeff
        }
    };
};


